import { Trans } from 'react-i18next';
import { useBillingStatus } from 'hooks/billing/useBillingStatus';
import { ChevronRight } from 'lucide-react';
import { CONVERSION_POPUP_TRIGGER_SOURCES } from 'services/mixpanel/consts';
import {
  BILLING_DANGER,
  BILLING_OVERAGE,
  BILLING_PILL_BORDER,
  BILLING_TEXT,
} from 'utils/billing/billingSurface';
import {
  BILLING_STATUS_KEYS,
  BILLING_STATUS_VARIANTS,
  type BillingStatusVariant,
} from 'utils/billing/deriveBillingStatus';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { BillingStatusChip } from 'components/Billing/BillingStatusChip';
import { ConversionPopupButton } from 'components/Billing/ConversionPopupButton';

const spawnPill = spawn('billing.pill');

/** Inline CTA classes for the non-overage (purple action) states. */
const EMPHASIS_CTA_CLASS =
  'text-text-action-emphasis-idle hover:text-text-action-emphasis-hover';

/**
 * Outline + label color per severity (Figma file Z1l7MGwOoVygv0LP7KMG97):
 * trial = purple #39277F outline + near-white label; warning (overage/
 * approaching) = amber #855614 outline + #E5BB7A label; danger = maroon #611D30
 * outline + destructive label.
 */
const VARIANT_CLASSES: Record<
  BillingStatusVariant,
  { border: string; text: string; dot: string }
> = {
  emphasis: {
    border: BILLING_PILL_BORDER.trial,
    text: BILLING_TEXT.offWhite,
    dot: 'bg-border-panel-emphasis',
  },
  warning: {
    border: BILLING_OVERAGE.border,
    text: BILLING_OVERAGE.pillText,
    dot: 'bg-orange-300',
  },
  danger: {
    border: BILLING_PILL_BORDER.danger,
    text: BILLING_DANGER.text,
    dot: BILLING_DANGER.dot,
  },
};

/**
 * Always-on billing status tag for the app top bar — one of the 3 self-serve
 * state surfaces. Figma: a 6px-radius outlined tag (not a full pill), ~33px
 * tall, with an IBM Plex Mono uppercase label, a purple dot separator, and an
 * inline Inter CTA that opens the conversion popup. Self-gates: renders nothing
 * on healthy/paid or for non-self-serve users (useBillingStatus → null).
 */
export const BillingStatusPill = () => {
  const status = useBillingStatus();

  if (!status) {
    return <></>;
  }

  const tone = VARIANT_CLASSES[status.variant];

  const overageCta =
    status.variant === BILLING_STATUS_VARIANTS.warning ||
    status.variant === BILLING_STATUS_VARIANTS.danger;

  return (
    <div
      className={cn(
        'flex items-center rounded-md border bg-transparent pl-4 pr-2 py-1.5 h-8',
        tone.border,
      )}
    >
      <BillingStatusChip colorClass={tone.text}>
        {status.key === BILLING_STATUS_KEYS.trial ||
        status.key === BILLING_STATUS_KEYS.migration ? (
          <Trans
            i18nKey={`billing.pill.${status.key}`}
            count={status.interpolation?.count as number}
            components={{
              dot: (
                <span
                  className={cn('inline-block size-1 rounded-full', tone.dot)}
                />
              ),
            }}
          />
        ) : (
          spawnPill(status.key, status.interpolation)
        )}
      </BillingStatusChip>
      <ConversionPopupButton
        type="button"
        variant="link"
        triggerSource={CONVERSION_POPUP_TRIGGER_SOURCES.STATUS_PILL}
        className={cn(
          '[&>svg]:text-inherit',
          overageCta ? 'text-text-action-neutral-idle' : EMPHASIS_CTA_CLASS,
        )}
        size="xs"
        iconPosition="right"
        icon={ChevronRight}
      >
        {spawnPill(`cta.${status.cta}`)}
      </ConversionPopupButton>
    </div>
  );
};
